import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Comment, CommentPostResponse } from '../models/comments/types';
import { fetchCommentById } from '../services/comment';
import { AxiosResponse } from 'axios';
import {
  createToastMessage,
  ToastMessageType,
  useToastMessages,
} from '../components/v2/toaster/toaster';

type UseCommentFetcherProps = {
  contentId: number;
  commentId: string;
};

const useCommentFetcher = ({
  contentId,
  commentId,
}: UseCommentFetcherProps) => {
  const [comment, setComment] = useState<Comment>();
  const [isFetching, setIsFetching] = useState(true);

  const { t } = useTranslation();
  const { setToastMessage } = useToastMessages();

  const setMessage = useCallback(
    (message: ToastMessageType) => setToastMessage(createToastMessage(message)),
    [setToastMessage]
  );

  const fetchComment = async () => {
    setIsFetching(true);

    try {
      const res = await fetchCommentById(contentId, commentId);
      handleFetchSuccess(res);
    } catch (err) {
      if (_.get(err, 'response')) {
        handleFetchFailure();
      } else {
        throw err;
      }
    }
  };

  const handleFetchSuccess = (res: AxiosResponse<CommentPostResponse>) => {
    const comment = {
      ...res.data.data.attributes,
      id: res.data.data.id,
    };

    setComment(comment);
    setIsFetching(false);
  };

  const handleFetchFailure = () => {
    setMessage({ text: t('comments.errors.fetch'), type: 'error' });
    setIsFetching(false);
  };

  useEffect(() => {
    void fetchComment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    comment,
    isFetching,
  };
};

export default useCommentFetcher;
